import { onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute } from 'vue-router'
import { sendUserHeartbeat } from '@/api/user.api'
import useImpersonation from '@/composables/useImpersonation'
import { isOnDevTestingRoute } from '@/utils/devHarnessHeaders'

const HEARTBEAT_INTERVAL_MS = 60_000

/**
 * Ping the backend while the product tab is visible so idle users still count
 * as active. Skipped during staff impersonation and while on `/dev/testing`
 * so those sessions do not inflate product metrics.
 */
export function useActivityHeartbeat(isAuthenticated: () => boolean) {
    const { isImpersonating } = useImpersonation()
    const route = useRoute()
    let intervalId: ReturnType<typeof setInterval> | null = null

    function shouldSkip(): boolean {
        return isImpersonating() || isOnDevTestingRoute()
    }

    function stop() {
        if (intervalId !== null) {
            clearInterval(intervalId)
            intervalId = null
        }
    }

    async function ping() {
        if (!isAuthenticated() || shouldSkip()) return
        try {
            await sendUserHeartbeat()
        } catch {
            // Non-critical; API traffic still updates last_active_at.
        }
    }

    function startIfVisible() {
        stop()
        if (!isAuthenticated() || shouldSkip()) return
        if (document.visibilityState !== 'visible') return

        void ping()
        intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
                void ping()
            }
        }, HEARTBEAT_INTERVAL_MS)
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'visible') {
            startIfVisible()
        } else {
            stop()
        }
    }

    onMounted(() => {
        startIfVisible()
        document.addEventListener('visibilitychange', onVisibilityChange)
    })

    // Entering/leaving the harness should start or stop heartbeats immediately.
    watch(
        () => route.path,
        () => {
            if (document.visibilityState === 'visible') {
                startIfVisible()
            } else {
                stop()
            }
        },
    )

    onBeforeUnmount(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        stop()
    })
}
